import { resolveRowUids } from "../../helpers/treeRowUids";
import { isStreamingPipelineEntry } from "../../helpers/streamingEntries";
import React from "react";
import {
  MDBBox,
  MDBBtn,
  MDBBtnGroup,
  MDBIcon,
  MDBListGroup,
  MDBListGroupItem,
} from "mdbreact";
import RecordFieldRows from "../../components/Viewer/RecordFieldRows";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setCompanyUsers, setProjects, setSidebarStatus } from "actions/user";
import { IFC_Url, url } from "api";
import { liveManifestUrl, legacyManifestUrl } from "helpers/liveManifest";
import { getCompressedJsonFile } from "api/files/getJsonFile";
import { getGridSource } from "api/glance/gridSource";
import { loadBuildingGridData } from "helpers/buildingGridLoad";
import { resolveToElement } from "webgl/js/loader/streaming/nestedParts";
import { findClickedMesh } from "helpers/treePathKeys";
import { unstable_batchedUpdates } from "react-dom";
import { reportClientError } from "helpers/clientLog";
import { scrollTargetId } from "helpers/treePathKeys";
import { scrollRowToCentre } from "helpers/scrollRowToCentre";
import { startTrace } from "helpers/clickTrace";
import { treeRoots, treeChildren, treeLocate, openKeysFromLocate, leafKeyFromLocate, leafRowIdFromLocate, serverBranch } from "helpers/modelTreeApi";
import { getProjects } from "api/projects/getProjects";
import useProjectFiles from "hooks/useProjectFiles";
import { getProjectModelDocuments } from "api/projects/modelSources";
import TreeViewerItem from "components/TreeViewerItem";
import useBranchGeometry from "hooks/useBranchGeometry";
import Loading from "components/ui-components/loading";
import { generateId } from "helpers/id";
import { buildDocumentSets, flattenRecords } from "helpers/documentSets";
import {
  ensureLeafForRecord,
  fillLazyNode,
  failLazyNode,
  findLazyNodeByKey,
  generateIndexTree,
} from "helpers/recordIndexTree";
import { scopeTreeIds } from "helpers/stableNodeId";
import { buildPrefixOwners, exclusivePrefixes, documentForUid, whyNotHideable } from "helpers/documentPrefixOwners";
import {
  expandComponent,
  findParams,
  chunkSpecFor,
  isRecordTooLarge,
  recordTooLargeMessage,
  findRecord,
  registerDocument,
} from "helpers/recordWorker";
import { ensure as ensureSourceFilter } from "webgl/js/loader/streaming/sourceFilterRegistry";
import { Maybe } from "helpers/maybeFunctor";
import addNotification from "helpers/notify";
import {
  findRowForRecord,
  recordFromClick,
  generateRawMeshes,
  generateRawTree,
} from "helpers/three";
import { useHttpWithCache } from "hooks/useHtthWithCache";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import TreeViewMenu from "react-simple-tree-menu";
import ModelFilterBar from "components/ModelFilterBar";
import ResizablePanels from "resizable-panels-react";
import WebGlApp from "webgl";
import NoMeshModal from "../../components/NoMeshModal";
import { useHttp } from "../../hooks/useHttp";

import { useLogout } from "hooks/useLogout";
import "../ModelViewer/TimelineViewer.scss";

import { ReactComponent as CloseIcon } from "../../assets/images/down-arrow.svg";

import estimationImg from "assets/images/estimationImg.png";
import projectIcon from "assets/images/projectIcon.png";

import ModelResizableViewer from "components/hoc/resizable-viewer/modelResizable-viewer";
import { QuantityContext } from "../../hooks/quantityContext";
import Filters from "components/Share/Filters";
import { applyDiffOverlay, diffLegendRows, diffElementRows, diffCoverageNote } from "helpers/diffOverlay";
import { Drawer, message as antdMessage } from "antd";
import DiffElementPanel from "components/Coordination/DiffElementPanel";
import { createIssueFromDiff } from "helpers/issueFromDiff";
import { getCommitDiff, getCommitEditFragments, approveRevision, rejectRevision } from "api/commits/commitsApi";
import { useRememberRouteProject } from "helpers/projectSelection";
import ViewerEffectsButton from "components/Viewer/ViewerEffectsButton";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import LiveRevisionWatch from "components/Viewer/LiveRevisionWatch";

// Classify a pathsmapping entry as a streaming-pipeline file.
//
// Background: the new R2 upload pipeline (R2FileUploadUtility) writes ALL
// files it uploads — including streaming-pipeline files — into the
// project's pathsmapping/paths arrays. The streaming viewer
// (StreamingLoader) consumes manifest.json + tiles/* + fragments/*
// directly via `manifestUrl`; it doesn't need ModelViewer's per-JSON
// loop to fetch them.
//
// Without this filter, the per-JSON loop below treats every
// tiles/tile_X.json.gz entry as if it were a legacy project JSON
// (Project1.json, _AdditionalParam.json, ...) and fires four
// getCompressedJsonFile calls per entry — one real, three for the
// non-existent _AdditionalParam / _ElectricalCircuits / _MechanicalSystems
// siblings (which serialize to fetches at /undefined). For a project
// with 850 tiles that's ~3,400 wasted requests, all serialized via
// `await`, blocking WebGlApp construction by 5+ minutes.
//
// Mirrors the C# IsStreamingPipelineFile classifier in
// R2FileUploadUtility — the two MUST stay in sync. If the exporter
// adds a new streaming-pipeline file type, both classifiers need
// updating.
//
// Inputs are the entry's filename portion (e.g. "tiles/tile_1_2.json.gz",
// "Project1.json.gz", "manifest.json.gz"). Returns true for streaming
// files that ModelViewer should NOT iterate.
// Moved to helpers/streamingEntries so Find in Sheets & Model reads the
// same document list (2026-10-29).

// Named, so the call site says WHY rather than naming a react-dom internal.
const batch = (fn) => unstable_batchedUpdates(fn);

const ModelViewer = (props) => {
  const { projectId, token, line, companyId } = props;
  // Signed-in viewing makes this the person's last project (not a share link).
  useRememberRouteProject(token ? "" : projectId);
  const dispatch = useDispatch();
  const logOut = useLogout();
  const viewerRef = useRef();
  const WebGL = useRef();
  // The viewer goes with the page: its streaming session, render loop,
  // window listeners and GL context (WebGlApp.dispose).
  useEffect(() => () => {
    try { if (WebGL.current && typeof WebGL.current.dispose === "function") WebGL.current.dispose(); } catch (e) { /* best effort */ }
    WebGL.current = null;
  }, []);
  const history = useHistory();
  const location = useLocation();
  const resizeRef = useRef();
  const [currencysymb, setCurrencysymb] = useState("");
  // ?diff=<commitId> — review a specific push on top of this model.
  // Opened from the Review Center's Revisions tab ("Review in 3D"), and
  // from anywhere else that wants to show what one export changed. The
  // whole viewer (tree, isolate, hide/show, section, measure) stays
  // available; the diff is an overlay on it, not a separate mode.
  const diffCommitId = useMemo(() => {
    try {
      return new URLSearchParams(location.search || "").get("diff") || "";
    } catch (e) {
      return "";
    }
  }, [location.search]);
  const [diffData, setDiffData] = useState(null);
  const [diffListOpen, setDiffListOpen] = useState(false);
  const [creatingDiffIssue, setCreatingDiffIssue] = useState(false);

  // Which element the diff list has isolated, if any. Isolation HIDES
  // everything else (it is the viewer's own isolate, not the overlay's
  // ghost), so the page has to remember it is in that state and offer
  // the way out - there was none, and the only escape was a reload.
  const [diffIsolatedId, setDiffIsolatedId] = useState(null);

  // Ask the viewer to show one element on its own. The tree already
  // knows how to isolate; this reuses it rather than inventing a second
  // way to do the same thing.
  // Is the filter bar open? The owner asked for a button on top with a bar
  // that more controls go into later, so the bar is a peer of the header
  // rather than a panel inside the viewer.
  const [filterOpen, setFilterOpen] = useState(false);

  // The same store, the same isolate, for a LIST of ids. The filter bar asks
  // the server what matched and hands the answer here; it never works out
  // what matches by itself.
  const isolateUniqueIds = useCallback((uniqueIds) => {
    try {
      const v = WebGL.current && WebGL.current.viewer;
      const store = v && (v.streaming?.state || v.stateStore);
      if (store && typeof store.isolate === "function") {
        store.isolate(uniqueIds);
        return true;
      }
      antdMessage.warning("This model cannot be isolated yet - wait for it to finish loading");
      return false;
    } catch (e) {
      antdMessage.error("Could not apply the filter to the model");
      return false;
    }
  }, []);

  const isolateDiffElement = (uniqueId) => {
    try {
      const v = WebGL.current && WebGL.current.viewer;
      const store = v && (v.streaming?.state || v.stateStore);
      if (store && typeof store.isolate === "function") {
        store.isolate([uniqueId]);
        setDiffIsolatedId(uniqueId);
      } else if (v && typeof v.onClickChildInList === "function") {
        v.onClickChildInList(uniqueId);
        setDiffIsolatedId(uniqueId);
      }
    } catch (e) {
      antdMessage.error("Could not isolate that element");
    }
  };

  // Back to the whole model. Clears the isolation AND anything the
  // reviewer hid on the way through, and leaves the diff overlay itself
  // alone - the change stays coloured and the rest stays ghosted, which
  // is the state the panel promises.
  const showEverythingInDiff = useCallback(() => {
    try {
      const v = WebGL.current && WebGL.current.viewer;
      const store = v && (v.streaming?.state || v.stateStore);
      if (store && typeof store.clearIsolation === "function") store.clearIsolation();
      // showAll() is the store's "unhide everything" - there is no
      // clearHidden. Isolation and hidden are separate buckets and a
      // reviewer who hid a wall on the way in expects it back too.
      if (store && typeof store.showAll === "function") store.showAll();
    } catch (e) { /* nothing to clear */ }
    setDiffIsolatedId(null);
  }, []);

  // Escape is the reflex for "get me out of this view".
  useEffect(() => {
    if (!diffIsolatedId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") showEverythingInDiff(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [diffIsolatedId, showEverythingInDiff]);

  // The snapshot is the point: an issue raised from a diff should carry
  // the picture that made it worth raising.
  const raiseIssueFromDiff = async () => {
    setCreatingDiffIssue(true);
    try {
      const { issue, href, snapshotFailed, cancelled } = await createIssueFromDiff({
        projectId,
        viewer: WebGL.current && WebGL.current.viewer,
        diff: diffData,
        focus: diffElementRows(diffData).slice(0, 12),
      });
      if (cancelled) return;
      antdMessage.success(
        snapshotFailed
          ? `Issue created, but the view could not be captured`
          : `Issue created with the view attached`
      );
      window.open(href || `/issue-viewer/${issue._id}`, "_blank");
    } catch (e) {
      antdMessage.error(e.message || "Could not create the issue");
    } finally {
      setCreatingDiffIssue(false);
    }
  };
  const [diffBusy, setDiffBusy] = useState("");
  const [diffHidden, setDiffHidden] = useState(false);
  // Which change bucket the panel is filtered to. null = all of them.
  const [diffBucket, setDiffBucket] = useState(null);
  // Both generations of every changed fragment - what lets the panel draw
  // a before/after picture. Best-effort: the panel works without it.
  const [editFragments, setEditFragments] = useState(null);
  const projectsRedux = useSelector((s) => s.user.projects);
  const usersRedux = useSelector((s) => s.user.users);
  const selectedMesh = useRef(null);
  const companiesRedux = useSelector((s) => s.mainAdmin.company_projects);

  const companiesProjectRedux = companiesRedux?.find((item) => item?._id === companyId)?.projects

  const listProject = Maybe.of(
    location.pathname.split('/')[1] === 'main-admin' ? companiesProjectRedux : projectsRedux
  )
    .map((projects) => projects?.filter((project) => project?._id === projectId)[0])
    .value || null;

  // PAGE BOOT (2026-09-17). The viewer used to start only once the whole
  // projects list had arrived - 7.8 MB, 4-27 s on SpaceX's company - just to
  // find this one project. The list is now slim (no model file lists), and
  // this project comes from GET /projects/:projectId (~0.3 s), so the viewer
  // starts from that; it is only ever initialised once (isGLOpend).
  const project = useProjectFiles(projectId, listProject);
  

  useEffect(() => {
    isMountedRef.current = true;
    if (isMountedRef.current && project && !isGLOpend) {
      setGLOpend(true);
      initializeAPI();
    }
    return () => (isMountedRef.current = false);
  }, [project]);

  const isMountedRef = useRef(null);

  const [clickedChild, setClickedChild] = useState([]);
  const [percentsLoaded, setPercentsLoaded] = useState(0);
  const [allowToResize, setAllowToResize] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState(false);
  // WHY there is nothing to show, in the user's words. Empty keeps the old
  // sentence for any caller that has not been given a reason yet.
  const [noMeshReason, setNoMeshReason] = useState('');
  const [isPopover, setIsPopOver] = useState(false);
  const [popoverCoords, setCoordsForPopover] = useState({
    top: "0px",
    left: "0px",
  });
  //
  const [resize, setResize] = useState(null);
  const [isUnmatchedMeshes, setIsUnmatchedMeshes] = useState(false);
  //
  const [isExistingMeshes, setIsExistingMeshes] = useState(false);
  const [isDemolitionMeshes, setIsDemolitionMeshes] = useState(false);
  //
  const [isSecondScreen, setSecondScreen] = useState(true);
  const [isFirstScreen, setFirstScreen] = useState(true);

  const [selectGroups, setSelectGroups] = useState([]);
  const [isGLOpend, setGLOpend] = useState(false);

  const [menu, setMenu] = useState(false);

  // project files variables
  let filesMeshes = [];
  let treeFilesMeshes = [];

  let additionalMeshes = [];
  let electricalMeshes = [];
  let mechanicalMeshes = [];
  let pipingMeshes = [];
  let switchMeshes = [];

  let [modelNumber, setModelNumber] = useState(0);
  let [modelsNames, setModelsNames] = useState([]);
  let [jsons, setJsons] = useState([]);
  let [models, setModels] = useState([]);
  let [additionalParams] = useState([]);
  let [electricalCircuits] = useState([]);
  let [mechanicalSystems] = useState([]);
  let [pipingSystems] = useState([]);
  let [switchSystems] = useState([]);
  // A SCROLL REQUEST, NOT A ROW ID. Holding the bare key meant clicking the
  // same element twice set state to the value it already had: React bailed
  // out, the effect below never re-ran, and the second click simply did not
  // scroll. Every request carries its own number, so asking again is always a
  // new request. `requestScroll` is the only way this is set.
  const scrollSeqRef = useRef(0);
  const [scrollId, setScrollId] = useState(null);
  const requestScroll = (key) => {
    if (!key) return;
    scrollSeqRef.current += 1;
    setScrollId({ key, n: scrollSeqRef.current });
  };

  const [treeFilesData, setTreeFilesData] = useState([]);
  const [filesOpenNodes, setFilesOpenNodes] = useState([]);
  const [filesMeshesArray, setFilesMeshes] = useState(null);

  const [additionalMeshesArray, setAdditionalMeshes] = useState(null);
  const [electricalMeshesArray, setElectricalMeshes] = useState(null);
  const [mechanicalMeshesArray, setMechanicalMeshes] = useState(null);
  const [pipingMeshesArray, setPipingMeshes] = useState(null);
  const [switchMeshesArray, setSwitchMeshes] = useState(null);

  const [isFilesMeshes, setIsFilesMeshes] = useState(false);
  const [clickedFilesChild, setClickedFilesChild] = useState([]);

  const [FiltersArr, setFiltersArr] = useState([]);
  const [additionalData, setAdditionalData] = useState([]);

  const meshQuantityInfoRef = useRef([]);

  let FilesMeshesForFilters = [];

  useEffect(() => {
    // FiltersArr is consumed by Filters.jsx via hasOwnProperty checks
    // — each key here gets surfaced as a filter source. Wrapping each
    // MEP array in another array matches the shape Filters expects:
    // `item.ElectricalCircuits[0]?.flatMap(...)`.
    const filtersPayload = [
      { ProjectFiles: treeFilesData.filter((item) => item) },
      { UnpricedComponents: [] },
      { ExistingComponents: [] },
      { DemolitionComponents: [] },
      { PricedComponents: [] },
      { AdditionalParams: additionalData.filter((item) => item) },
    ];
    if (electricalMeshesArray) {
      filtersPayload.push({ ElectricalCircuits: electricalMeshesArray });
    }
    if (mechanicalMeshesArray) {
      filtersPayload.push({ MechanicalSystems: mechanicalMeshesArray });
    }
    if (pipingMeshesArray) {
      filtersPayload.push({ PipingSystems: pipingMeshesArray });
    }
    if (switchMeshesArray) {
      filtersPayload.push({ SwitchSystems: switchMeshesArray });
    }
    setFiltersArr(filtersPayload);
  }, [
    treeFilesData,
    additionalData,
    electricalMeshesArray,
    mechanicalMeshesArray,
    pipingMeshesArray,
    switchMeshesArray,
  ]);

  useEffect(() => {
    if (menu) {
      resizeRef.current.state.panelsSize = [100];
    } else {
      resizeRef.current.state.panelsSize = [0];
    }
  }, [menu]);

  // Everything a document's records are needed for, loaded when it is
  // needed. See helpers/recordWorker.js for why they are not loaded here.
  // WHAT A 3D CLICK CAN ACTUALLY SEE (owner, 2026-10-20: "There is no model
  // associated with this component, although all data is there it's just not
  // loaded").
  //
  // `filesMeshes` and `treeFilesMeshes` are declared `let ... = []` in the
  // component body, so React empties them on EVERY render. They are filled
  // while the JSON downloads and copied into state at onLoad; from the next
  // render onward the raw arrays are empty. And the viewer stores this page's
  // callbacks ONCE (webgl/js/viewer/index.js:509), so findInJsonUsingViewer is
  // the closure from mount - reading the STATE inside it would read the
  // mount-time values forever. A ref is the one shape a frozen closure can
  // still look inside, which is why lazyDocsRef and prefixOwnersRef right here
  // already work while the two arrays beside them do not.
  //
  // This is what made the lazy path fail too: findLazyNodeByKey was handed
  // treeFilesMeshes[docIndex] from an EMPTY array, so the record was found and
  // the row was never opened.
  const filesMeshesRef = useRef([]);
  // THE TREE THE SERVER SERVES, for an export that has no legacy per-document
  // JSONs (owner, 2026-10-20). Held in a ref as well as state for the same
  // reason as the arrays above: the viewer captured this page's callbacks at
  // mount, so a click can only see a ref.
  const serverTreeRef = useRef(null);
  const [serverTree, setServerTree] = useState(null);
  const [serverLeafKey, setServerLeafKey] = useState("");
  const treeFilesRef = useRef([]);
  const lazyDocsRef = useRef([]);
  // An element's uniqueId names its own document: the first 36 characters
  // are that document's GUID. This maps those prefixes onto the worker's
  // document handles, so a click asks ONE document for the record rather
  // than asking each in turn - which, on a 17-document project whose host
  // record file is 306 MB, meant parsing hundreds of megabytes before the
  // details panel could say anything.
  // prefix -> which file, with the prefixes two files share held apart
  // (helpers/documentPrefixOwners).
  const prefixOwnersRef = useRef(buildPrefixOwners([]));
  // FILES THIS EXPORT HOLDS NO ELEMENTS FOR. Without this they look exactly
  // like a file whose prefixes have not loaded yet, and the eye tells the
  // person to "try again once the model has finished loading" - advice that
  // will never come true, because there is nothing to wait for.
  const emptyDocsRef = useRef(new Set());

  const loadDocumentRecords = async (sets, base) => {
    const isIfc = project?.isIfcProject ? project?.isIfcProject : false;
    const lazyDocs = [];

    const publishTree = () => {
      const built = [...treeFilesMeshes].filter((item) => item);
      // THE REF KEEPS THE UNFILTERED ARRAY. treeFilesData is filtered, so its
      // indices are compacted - and the lazy path indexes by docIndex, which
      // counts documents including the empty slots. Mirroring the raw array
      // here keeps docIndex meaning what it meant when it was issued.
      treeFilesRef.current = treeFilesMeshes;
      setTreeFilesData(built);
      setFilesOpenNodes((s) => {
        const roots = built.map((tv) => tv[0]?.key).filter(Boolean);
        const next = new Set([...(s || []), ...roots]);
        return [...next];
      });
    };

    for (let i = 0; i < sets.length; i++) {
      const set = sets[i];

      // Indexed document: tree from the index, records on demand.
      if (set.treeIndex) {
        const docId = `${projectId}:${i}`;
        const slices = {};
        Object.keys(set.paramSlices || {}).forEach((token) => {
          slices[token] = `${base}/${set.paramSlices[token]}`;
        });
        let index = null;
        try {
          index = await getCompressedJsonFile(set.treeIndex, isIfc);
        } catch (e) {
          index = null;
        }
        // Registered after the index is read: the index says whether the
        // tree comes in chunks, and the worker needs that to answer the
        // first click with one piece instead of the whole file.
        registerDocument(docId, `${base}/${set.tree}`, slices, chunkSpecFor(index, set, base));
        if (index && index.SubProjects) {
          treeFilesMeshes[i] = generateIndexTree(index, docId);
          filesMeshes[i] = [];
          lazyDocs.push({ docId, docIndex: i });
          publishTree();
          // The unsplit _AdditionalParam is what the slices replace -
          // 264 MB of it on SpaceX's host document - so it is skipped
          // here; the other companions are small and still load.
          await loadCompanionFiles(set, i, isIfc, { skipAdditionalParam: true });
          continue;
        }
        // No usable index: fall through and load the records.
      }

      try {
        const res = await getCompressedJsonFile(set.tree, isIfc);
        filesMeshes[i] = generateRawMeshes(res);
        // Per-file scope: each file's $id restarts at "1" (helpers/stableNodeId).
        treeFilesMeshes[i] = scopeTreeIds(generateRawTree(res), `${projectId}:${i}`);
        publishTree();
      } catch (e) {}
      await loadCompanionFiles(set, i, isIfc, {});
    }

    lazyDocsRef.current = lazyDocs;

    // Learn which prefixes belong to which document. Paired on the record
    // tree path, which is the one identifier both sides agree on: the
    // endpoint's own `id` is the exporter's DocumentId and does not
    // appear in a uniqueId at all.
    if (lazyDocs.length) {
      try {
        const answer = await getProjectModelDocuments(projectId);
        const byTree = new Map();
        (answer?.documents || []).forEach((d) => {
          if (d.treePath) byTree.set(String(d.treePath), d);
        });
        // A PREFIX TWO FILES BOTH CLAIM BELONGS TO NEITHER
        // (helpers/documentPrefixOwners). This was `map.set(prefix, docId)`,
        // so a shared prefix went to whichever document came last - and then
        // hiding a linked file took the host's elements with it, which is
        // most of the model. See the helper for why this hit some people and
        // not others on the same project.
        prefixOwnersRef.current = buildPrefixOwners(lazyDocs.map(({ docId, docIndex }) => ({
          docId,
          prefixes: (byTree.get(String(sets[docIndex]?.tree)) || {}).prefixes || [],
        })));
        // Read from the same records, so "empty" and "prefixes unknown" can
        // never disagree: a count of 0 is a fact the export states, while a
        // missing count is simply not knowing, and only the first is said.
        const empty = new Set();
        lazyDocs.forEach(({ docId, docIndex }) => {
          const doc = byTree.get(String(sets[docIndex]?.tree));
          if (doc && Number(doc.elementCount) === 0) empty.add(docId);
        });
        emptyDocsRef.current = empty;
      } catch (e) {
        // Without the map a click falls back to asking every document,
        // which is slow but still correct.
        prefixOwnersRef.current = buildPrefixOwners([]);
        emptyDocsRef.current = new Set();
      }
    }
    filesMeshesRef.current = filesMeshes;   // same slots, same order as docIndex
    setFilesMeshes([...filesMeshes]);
    setAdditionalMeshes(flattenRecords(additionalMeshes));
    setElectricalMeshes(flattenRecords(electricalMeshes));
    setMechanicalMeshes(flattenRecords(mechanicalMeshes));
    if (pipingMeshes.length > 0) setPipingMeshes(flattenRecords(pipingMeshes));
    if (switchMeshes.length > 0) setSwitchMeshes(flattenRecords(switchMeshes));
    publishTree();
  };

  const loadCompanionFiles = async (set, i, isIfc, { skipAdditionalParam }) => {
    const grab = async (path, sink) => {
      if (!path) return;
      try {
        sink[i] = await getCompressedJsonFile(path, isIfc);
      } catch (e) {}
    };
    if (!skipAdditionalParam) await grab(set.additionalParam, additionalMeshes);
    await grab(set.electricalCircuits, electricalMeshes);
    await grab(set.mechanicalSystems, mechanicalMeshes);
    await grab(set.pipingSystems, pipingMeshes);
    await grab(set.switchSystems, switchMeshes);
  };

  // The building's own grid lines, for the G button.
  //
  // NOTHING IS FETCHED UNTIL SOMEBODY PRESSES G. This used to run at
  // model load, "off the critical path" - and it was not. There is one
  // grid file PER DOCUMENT and they were fetched in SERIES: on BA03,
  // seventeen gzipped round-trips plus the grid-source call, every
  // session, competing with the streaming loader for the network and the
  // main thread at exactly the moment the model is coming in. For a
  // control most sessions never touch. That is a load-time cost I added
  // on 2026-09-22 and it is part of why the owner reports the models
  // became heavy (2026-09-23).
  //
  // So the page hands the viewer a LOADER instead of data, and the
  // viewer calls it on the first G press. When it resolves,
  // setBuildingGrid builds and shows the grid, so a press that arrives
  // before the data is not lost.
  //
  // WHICH FILES: the grid source's file, whether or not its model is shown -
  // see helpers/buildingGridLoad. Hiding BA04 Core Shell on BA03 used to
  // leave two grids and no answer, and G drew the generic lattice.
  const loadBuildingGrid = (allSets, shownSets, isIfc) => loadBuildingGridData({
    allSets,
    shownSets,
    getSource: () => getGridSource(projectId),
    read: (path) => getCompressedJsonFile(path, isIfc),
  });

  // Open one aggregated row: ask the worker for its elements and swap
  // them in for the placeholder. Shared by the tree (a click on the
  // expander) and by a click in the 3D that lands on a row nobody has
  // opened yet.
  // A Category/Group row's elements, when its rows below are still
  // placeholders (helpers/treeRowUids): confined to the row's document by
  // the UniqueId prefixes learned for it.
  // A file's UniqueId prefixes (learned in loadDocumentRecords): what the
  // eye on its root row hides.
  // ONLY WHAT IS THIS FILE'S ALONE. A prefix it shares with another file is
  // not an answer to "which elements are this file's", and hiding on one is
  // how the 3D went blank.
  const documentPrefixes = (docId) => exclusivePrefixes(prefixOwnersRef.current, docId);
  const documentIsEmpty = (docId) => emptyDocsRef.current.has(docId);
  const resolveTreeRow = (node) => resolveRowUids(projectId, node, {
    roots: (treeFilesData || []).flat(),
    loadLazy: loadLazyNode,
    prefixesOf: documentPrefixes,
  });

  // GEOMETRY FOR THE BRANCH THAT IS OPEN (hooks/useBranchGeometry). The mesh
  // icon on a row means only "the viewer has not built this", which on a
  // streaming export is most rows most of the time - so opening a branch, or
  // landing on one from a 3D click, shows a column of icons that read as a
  // broken model. This asks for the tiles that branch lives in and lets the
  // rows say which of the three facts they are standing for.
  const branchGeometry = useBranchGeometry({
    projectId,
    sections: (treeFilesData && treeFilesData.length) ? treeFilesData : serverTree,
    openNodes: filesOpenNodes,
    WebGL,
    // What a row stands for when its own rows are still placeholders - the
    // same resolver the eye and isolate use on this tree.
    resolveRowUids: resolveTreeRow,
  });

  const loadLazyNode = async (node) => {
    if (!node || !node.lazy || node.lazy.loaded || node.__pending) return node;
    node.__pending = true;
    try {
      const result = await expandComponent(node.lazy.doc, node.lazy.key, node.lazy.count);
      fillLazyNode(node, result);
    } catch (e) {
      failLazyNode(
        node,
        isRecordTooLarge(e)
          ? recordTooLargeMessage(node.lazy.doc, e)
          : "Could not load these elements"
      );
    } finally {
      node.__pending = false;
      setTreeFilesData((s) => [...s]);
    }
    return node;
  };

  const { request: initializeAPI } = useHttp({
    requestCallback: async (d) => {
      // Filter pass: streaming-pipeline files are uploaded to R2 by
      // R2FileUploadUtility but are consumed by StreamingLoader via
      // manifestUrl, NOT by this loop. If we let them through, every
      // tiles/tile_*.json.gz spawns 4 getCompressedJsonFile calls
      // (one real + three for the undefined _AdditionalParam /
      // _ElectricalCircuits / _MechanicalSystems siblings), serialized
      // via await, which blocks WebGlApp construction by minutes for
      // any project that uses streaming.
      //
      // We filter both pathsmapping AND paths in the same step so they
      // stay index-aligned (both arrays are zipped by index in the
      // loop below).
      const rawMapping = project?.zipInfo?.pathsmapping ?? [];
      const rawPaths = project?.zipInfo?.paths ?? [];
      const legacyMapping = [];
      const legacyPaths = [];
      for (let i = 0; i < rawMapping.length; i++) {
        if (isStreamingPipelineEntry(rawMapping[i])) continue;
        legacyMapping.push(rawMapping[i]);
        legacyPaths.push(rawPaths[i]);
      }

      // Pair each document with ITS OWN component files, by name.
      //
      // This used to walk the list positionally: every .json entry counted
      // as a document, and jsons[i] was paired with additionalParams[i].
      // Those indices line up only when there is exactly one document.
      // Measured on a real 3-document production export, the loop ran six
      // times for three documents and gave document 2 the parameters
      // belonging to document 3. See helpers/documentSets.js.
      // Project "Model files" filter (gear on the project card): documents
      // the user unticked are left out of the Project Files tree, the
      // records lookups and (in the streaming loader) the 3D model.
      const sourceFilter = await ensureSourceFilter(projectId);
      const allDocumentSets = buildDocumentSets(legacyMapping, legacyPaths);
      const documentSets = allDocumentSets
        .filter((d) => !sourceFilter.isTreePathHidden(d.tree));

      modelNumber = documentSets.length;
      setModelNumber(modelNumber);
      modelsNames = documentSets.map((d) => d.name);
      setModelsNames(modelsNames);

      var baseURL = url;
      if (project?.isIfcProject) {
        baseURL = project.isIfcProject ? IFC_Url : url;
      }

      // Fresh arrays rather than push() onto the useState-held ones: those
      // references survive re-renders, so pushing accumulated duplicates
      // every time this ran.
      models = legacyPaths
        .filter((path) => path.endsWith(".glb") || path.endsWith(".glb.gz"))
        .map((path) => `${baseURL}/${path}`);
      jsons = documentSets.map((d) => d.tree);

      setModels(models);
      setJsons(jsons);

      // The 3D starts here, before a single record file is read.
      //
      // This construction used to sit BELOW a loop that awaited every
      // document's records, so on SpaceX BA03 the viewer waited for 51
      // files and more than 1.5 GB of JSON - downloaded, gunzipped and
      // parsed on the main thread - before it asked for its first
      // fragment. Nothing below needs a record file. The Project Files
      // tree and Component Details do, and they now load beside the
      // model instead of in front of it.
      // The live version's own manifest (helpers/liveManifest), not the
      // project-level copy a promote rewrites.
      const manifestUrl = baseURL === url ? await liveManifestUrl(projectId) : legacyManifestUrl(baseURL, projectId);
      // A new model replaces the old viewer; do not leave it running.
      try { if (WebGL.current && typeof WebGL.current.dispose === "function") WebGL.current.dispose(); } catch (e) { /* best effort */ }
      WebGL.current = new WebGlApp(
        viewerRef.current,
        {
          glbs: models,
          // Streaming activates because this URL is passed at all - the
          // choice is made on the PRESENCE of manifestUrl, not on what it
          // returns (streamingLoader.js:512). It hits the same /uploads/
          // route the legacy GLB pipeline uses, so there is no new backend
          // wiring.
          // NOT TRUE, though it used to be claimed here: there is no
          // fallback to the legacy Loader when the manifest 404s. A failed
          // init logs "[StreamingLoader] init failed" and sets the error
          // state; the legacy Loader is built only when no manifestUrl is
          // passed at all. (Corrected 2026-09-20 - the wrong comment cost
          // an hour of chasing the wrong loader for the owner's bug.)
          manifestUrl,
        },
        {
          setClickedChild,
          setLoading,
          setError,
          setPercentsLoaded,
          setAllowToResize,
          setModal,
          setCoordsForPopover,
          findInJsonUsingViewer,
          setIsPopOver,
          onHoverMesh,
          selectedMesh,
          project,
        },
        line,
        false,
        project?.isIfcProject ? project?.isIfcProject : false
      );

      // Records, off the critical path.
      //
      // A document that ships an index (`<Document>_TreeIndex.json.gz`)
      // gets its tree from the index - a few KB for SpaceX's 220,625
      // elements - and never parses its record file here at all. The
      // rows that stand for more than one element load through the
      // record worker when the user opens them, and the parameters
      // come from the per-category slices. A document without an index
      // (an older export) loads the way it always did, only now behind
      // the viewer rather than in front of it.
      loadDocumentRecords(documentSets, baseURL);
      // Registered, NOT run: the viewer calls it on the first G press.
      // See loadBuildingGrid - running it here cost every session
      // seventeen serial fetches for a control most never touch.
      const gridViewer = WebGL.current && WebGL.current.viewer;
      if (gridViewer && typeof gridViewer.setBuildingGridLoader === "function") {
        gridViewer.setBuildingGridLoader(() => loadBuildingGrid(allDocumentSets, documentSets, project?.isIfcProject));
      }
    },
    onLoad: () => {
      setAdditionalData(FilesMeshesForFilters);
    },
    onError: (e) => {
      console.error(e);
      setError(true);
    },
  });

  const {
    error: cacheError,
    loading: cacheLoading,
    errorStatus: cacheErrorStatus,
    request: cacheRequest,
    refresh,
  } = useHttpWithCache({
    requestCallback: () => getProjects(),
    reduxSetter: (data) => {
      dispatch(setProjects(data.projects));
      dispatch(setCompanyUsers(data.users));
    },
    reduxReset: () => {
      dispatch(setProjects(null));
      dispatch(setCompanyUsers(null));
    },
    reduxCash: {
      projects: projectsRedux,
      users: usersRedux,
    },
    allowToCash: true,
  });

  const onHoverMesh = (id) => {};

  const jsonInfo = Maybe.of(
    (() => {
      if (true) {
        return {
          neededMeshesArray: filesMeshesArray,
          neededClickedChild: clickedFilesChild,
        };
      }
    })()
  ).map(({ neededMeshesArray, neededClickedChild }) => {
    meshQuantityInfoRef.current = neededClickedChild;
    return neededClickedChild.length === 1
      ? neededClickedChild?.flatMap((i) => {
          return (
            neededMeshesArray?.flat()?.find((k) => k?.UniqueId === i?.UniqueId) ||
            // Indexed documents keep no flat array on this side; the
            // row the user clicked is the record.
            i
          );
        })[0]
      : neededClickedChild.reduce(
          (totalObject, mesh) => {
            const sumProps = Object.assign(
              {},
              {
                LaborInfoTime:
                  totalObject.LaborInfoTime + mesh.LaborInfo
                    ? mesh.LaborInfo?.map((l) => l.TotalTime)
                    : 0,
                EquipmentInfoTime:
                  totalObject.EquipmentInfoTime + mesh.EquipmentInfo
                    ? mesh.EquipmentInfo?.map((l) => l.TotalTime)
                    : 0,

                LaborInfoPrice:
                  totalObject.LaborInfoPrice + mesh.LaborInfo
                    ? mesh.LaborInfo?.map((l) => l.TotalPrice)
                    : 0,
                EquipmentInfoPrice:
                  totalObject.EquipmentInfoPrice + mesh.EquipmentInfo
                    ? mesh.EquipmentInfo?.map((l) => l.TotalPrice)
                    : 0,

                Area: totalObject.Area + mesh.Area,
                Count: totalObject.Count + mesh.Count,
                Length: totalObject.Length + mesh.Length,
                Quantity: totalObject.Quantity + mesh.Quantity,
                Volume: totalObject.Volume + mesh.Volume,
                TotalPrice: totalObject.TotalPrice + mesh.TotalPrice,
              },
              typeof totalObject.Width === "number"
                ? {
                    Width: totalObject.Width + mesh.Width,
                  }
                : {},
              typeof totalObject.Depth === "number"
                ? {
                    Depth: totalObject.Depth + mesh.Depth,
                  }
                : {},
              typeof totalObject.TotalAmount === "number"
                ? {
                    TotalAmount: totalObject.TotalAmount + mesh.TotalAmount,
                  }
                : {},
              typeof totalObject.Dimentions === "number"
                ? {
                    Dimentions: totalObject.Dimentions + mesh.Dimentions,
                  }
                : {}
            );

            const stringProps = Object.assign(
              {},
              totalObject.Uniformat === mesh.Uniformat && totalObject.Uniformat
                ? { Uniformat: "-" }
                : { Uniformat: mesh.Uniformat },
              totalObject.Name !== mesh.Name && totalObject.Name
                ? { Name: "-" }
                : { Name: mesh.Name },
              totalObject.Type !== mesh.Type && totalObject.Type
                ? { Type: "-" }
                : { Type: mesh.Type },
              totalObject.MainCategory !== mesh.MainCategory &&
                totalObject.MainCategory
                ? { MainCategory: "-" }
                : { MainCategory: mesh.MainCategory },
              totalObject.CategoryName !== mesh.CategoryName &&
                totalObject.CategoryName
                ? { CategoryName: "-" }
                : { CategoryName: mesh.CategoryName },
              totalObject.LaborInfo !== mesh.LaborInfo && totalObject.LaborInfo
                ? { LaborInfoType: "-" }
                : {
                    LaborInfoType: mesh.LaborInfo
                      ? mesh.LaborInfo?.map((l) => l.Description).join(" | ")
                      : "-",
                  },
              totalObject.EquipmentInfo !== mesh.EquipmentInfo &&
                totalObject.EquipmentInfo
                ? { EquipmentInfoType: "-" }
                : {
                    EquipmentInfoType: mesh.EquipmentInfo
                      ? mesh.EquipmentInfo?.map((l) => l.Description).join(
                          " | "
                        )
                      : "-",
                  }
            );

            if (!totalObject.LaborInfo || totalObject.LaborInfo == null) {
              totalObject.LaborInfo = [];
            }
            totalObject.LaborInfo.push(
              ...totalObject.LaborInfo,
              mesh.LaborInfo ? mesh.LaborInfo : null
            );

            if (
              !totalObject.EquipmentInfo ||
              totalObject.EquipmentInfo == null
            ) {
              totalObject.EquipmentInfo = [];
            }
            totalObject.EquipmentInfo.push(
              ...totalObject.EquipmentInfo,
              mesh.EquipmentInfo ? mesh.EquipmentInfo : null
            );

            if (!totalObject.MaterialInfo || totalObject.MaterialInfo == null) {
              totalObject.MaterialInfo = [];
            }

            totalObject.MaterialInfo.push(...totalObject.MaterialInfo, {
              Types: mesh.MaterialNames,
              Name: mesh.Type,
              TotalAmount: mesh.TotalAmount,
              TotalPrice: mesh.TotalPrice,
            });

            return { ...sumProps, ...stringProps };
          },
          neededClickedChild[0]
            ? {
                ...neededClickedChild[0],
                ...{
                  LaborInfoTime: 0,
                  EquipmentInfoTime: 0,
                  EquipmentInfoPrice: 0,
                  LaborInfoPrice: 0,
                  LaborInfo: [],
                  EquipmentInfo: [],
                  MaterialInfo: [],
                  Area: 0,
                  Count: 0,
                  Length: 0,
                  Quantity: 0,
                  Volume: 0,
                  TotalPrice: 0,
                },
              }
            : null
        );
  }).value;

  const [additionalFileInfo, setAdditionalFileInfo] = useState(null);
  const [electricalFileInfo, setElectricalFileInfo] = useState(null);
  const [mechanicalFileInfo, setMechanicalFileInfo] = useState(null);

  useEffect(() => {
    (async () => {
      const additionalMatchedData = await Maybe.of(
        (() => {
          if (true) {
            return {
              neededMeshesArray: additionalMeshesArray,
              neededClickedChild: clickedFilesChild,
            };
          }
        })()
      ).map(({ neededMeshesArray, neededClickedChild }) => {
        return neededClickedChild?.map((i) => {
          return neededMeshesArray?.find((j) => {
            return j?.UniqueId === i?.UniqueId;
          });
        });
      }).value;

      // Indexed documents do not load the unsplit _AdditionalParam at
      // all: it is 264 MB on SpaceX's host document. Their parameters
      // come one element at a time from the per-category slice the
      // exporter writes beside the records, read in the worker. The
      // token is the element's top category with anything outside
      // [A-Za-z0-9-] replaced, which is how the exporter names the file.
      const missing = (clickedFilesChild || []).filter(
        (row, index) => row && row.__docId && !additionalMatchedData?.[index]
      );
      if (missing.length) {
        const rows = await Promise.all(
          missing.map((row) => {
            const token = String(row.TopCategory || row.CategoryName || "Other").replace(
              /[^A-Za-z0-9-]/g,
              "_"
            );
            return findParams(row.__docId, row.UniqueId, token).catch(() => null);
          })
        );
        missing.forEach((row, i) => {
          if (!rows[i]) return;
          const at = clickedFilesChild.indexOf(row);
          if (at >= 0 && additionalMatchedData) additionalMatchedData[at] = rows[i];
        });
      }

      const electricalMatchedData = await Maybe.of(
        (() => {
          return {
            neededMeshesArray: electricalMeshesArray,
          };
        })()
      ).map(({ neededMeshesArray }) => {
        return additionalMatchedData?.map((i) => {
          return neededMeshesArray?.find((j) => {
            return j?.uniqueId === i?.EquipmentParameters?.ElectricalSystem;
          });
        });
      }).value;

      const mechanicalmatchedData = Maybe.of(
        (() => {
          if (true) {
            return {
              neededMeshesArray: mechanicalMeshesArray,
            };
          }
        })()
      ).map(({ neededMeshesArray }) => {
        return additionalMatchedData?.map((i) => {
          return neededMeshesArray?.find((j) => {
            return j?.uniqueId === i?.EquipmentParameters?.MechanicalSystem;
          });
        });
      }).value;

      setAdditionalFileInfo(additionalMatchedData?.filter((item) => item));
      setElectricalFileInfo(
        electricalMatchedData.filter(function (element) {
          return element !== undefined;
        })
      );
      setMechanicalFileInfo(
        mechanicalmatchedData.filter(function (element) {
          return element !== undefined;
        })
      );
    })();
  }, [clickedFilesChild]);

  const jsonFilesMeshesToJSX = useMemo(() => {
    // THE SERVER'S BRANCH WINS WHEN THE LEGACY LOOP HAD NOTHING. On a
    // streaming export treeFilesData is empty - there are no per-document
    // JSONs to build it from - and this is the only tree the page can show.
    // When both exist the legacy one is kept, so nothing changes for a
    // project still published the old way.
    // A STABLE TREE KEY. This was key={generateId()} - a uuid - so React tore
    // the tree down and built a new one on EVERY render, discarding the
    // expansion and the selection the 3D click had just set. The owner's
    // clicks all reported row-found: the record was located every time and the
    // tree was destroyed before he could see it. The identical line existed in
    // Viewer3d.jsx and was fixed there earlier today; I did not check this
    // page, which is the one he actually uses.
    const sections = (treeFilesData && treeFilesData.length) ? treeFilesData : (serverTree || []);
    return sections.map((section, mainIndex) => {
      // cacheSearch off: the library memoizes its tree walk under
      // JSON.stringify of the WHOLE tree + open nodes, in a module-level
      // cache that is never emptied - every expand or new tree added another
      // full copy of the tree as a string (1.2 GB of heap on the staging
      // spaceX-test viewer, 2026-09-18). Walking it again each render is
      // linear and cheap.
      return Maybe.of(section).map((tree) => (
        <TreeViewMenu cacheSearch={false} key={`files-tree-${mainIndex}`} openNodes={filesOpenNodes} data={tree}>
          {({ items }) => {
            return items.map((item, index) => {
              return (
                <TreeViewerItem
                  item={item}
                  index={index}
                  clickedChild={clickedFilesChild}
                  setClickedChild={setClickedFilesChild}
                  selectGroups={selectGroups}
                  setSelectGroups={setSelectGroups}
                  // A uuid here remounts every ROW on every render for the
                  // same reason as the tree above. The item's own key is
                  // stable and unique within its section.
                  key={item.key || item.id || index}
                  selectedMesh={selectedMesh}
                  WebGL={WebGL}
                  tree={tree}
                  setTreeData={(updatedSection) => {
                    [...treeFilesData][mainIndex] = updatedSection;
                    setTreeFilesData([...treeFilesData]);
                  }}
                  setOpenNodes={setFilesOpenNodes}
                  onExpandLazy={loadLazyNode}
                  onResolveRowUids={resolveTreeRow}
                  onDocumentPrefixes={documentPrefixes}
                  onWhyNotHideable={(docId) => whyNotHideable(prefixOwnersRef.current, docId)}
                  onDocumentIsEmpty={documentIsEmpty}
                  askedUids={branchGeometry.askedUids}
                  untiledUids={branchGeometry.untiledUids}
                  meshVersion={branchGeometry.meshVersion}
                  isUnmatchedMeshes={() => setIsUnmatchedMeshes(false)}
                  isExistingMeshes={() => setIsExistingMeshes(false)}
                  isDemolitionMeshes={() => setIsDemolitionMeshes(false)}
                  isFilesMeshes={() => setIsFilesMeshes(true)}
                  selectors={{
                    prevMeshes: "prevFilesMeshes",
                    prevMaterials: "prevFilesMaterials",
                    color: "green",
                  }}
                />
              );
            });
          }}
        </TreeViewMenu>
      )).value;
    });
    // THE DEPENDENCY WAS A FRESH ARRAY LITERAL every render, so this memo
    // never held - and it did not name serverTree at all, so the branch the
    // server returns would not have been drawn. Both fixed: the array
    // identity is what changes when the data changes.
    // branchGeometry is named because the rows read it: without it the memo
    // holds the old rows and a mesh that has just arrived is not drawn.
  }, [treeFilesData, serverTree, serverLeafKey, clickedFilesChild, filesOpenNodes, branchGeometry]);

  const pickMeshInNeededTree = (
    clicked,
    treeMeshes,
    setOpenNodes,
    setClickedChild,
    keyIsCtrl,
    selectors,
    tr,
    preferredNodeId
  ) => {
    const trace = tr || { step: () => {}, end: () => {} };
    trace.step("pick: entry", { sections: Array.isArray(treeMeshes) ? treeMeshes.length : 0 });
    setSelectGroups([]);
    if (!clicked) {
      trace.end("pick: nothing clicked");
      setModal(true);
    } else {
      // ONE ENTRY PER DOCUMENT, or one bare record: helpers/three
      // recordFromClick decides, so neither shape can throw or go missing.
      const newClicked = recordFromClick(clicked);

      let path;

      // ASK FOR THE IDENTITY THE NODE WAS BUILT WITH.
      //
      // This used to search for `node.id === $id` only, and the owner's trace
      // of 2026-10-21 shows what that costs: record found, lazy row found,
      // leaf inserted, and then path depth 0 - because the leaf's id is $id
      // ONLY when the export carried one. Three identities are tried, in the
      // order of how much they are worth: the id of the leaf the lazy path
      // just inserted (exact - we built it), the element's Revit UniqueId
      // (always present on a component), then $id for the eager trees that
      // have always worked this way.
      //
      // findRecursiveInTreeMatch also searches EVERY root rather than tree[0],
      // and takes the document trees as a list - the old call spread them, so
      // on a project with two documents the second tree was passed where the
      // id belonged and the search ran on nonsense.
      const found = findRowForRecord(treeMeshes, newClicked, preferredNodeId);
      path = found.path;
      trace.step("pick: match", { by: found.by, hadLeafId: found.hadLeafId, hadJsonId: found.hadJsonId });

      clicked = newClicked;

      console.log("path from pick needed mesh ", path);

      // NO PATH IS NOT NO SELECTION. The tree may not list the element (a
      // lazy row shows its first 2,000), and `path[path.length - 1].id`
      // then threw - after the highlight of a plain click, but BEFORE the
      // only highlight a ctrl-click gets, so a ctrl-click selected nothing
      // and the details stayed empty. Open and scroll what we can; the
      // selection and the details below do not depend on the tree.
      trace.step("pick: path", { depth: Array.isArray(path) ? path.length : 0 });
      if (!Array.isArray(path)) path = [];
      // One key per level, each the ancestors' keys joined - which is
      // how react-simple-tree-menu addresses a row. Built from the path
      // rather than from four hardcoded levels: an indexed document has
      // a fifth (the aggregated component row above its elements).
      const nodesKey = [];
      path.forEach((node, depth) => {
        nodesKey.push(depth === 0 ? node.id : `${nodesKey[depth - 1]}/${node.id}`);
      });
      trace.step("pick: nodesKey", { count: nodesKey.length, last: String(nodesKey[nodesKey.length - 1] || "").slice(0, 40) });
      // ONE UPDATE, NOT ONE PER LEVEL. Five keys meant five setState calls,
      // and because this runs after an await React 16 does not batch them -
      // each one rendered the whole tree on its own, and the library re-walks
      // the tree on every render (cacheSearch is off by necessity). Owner,
      // 2026-10-21: "when i click on something it hangs". His trace agrees:
      // setClickedChild alone took 2,924ms.
      setOpenNodes((s) => {
        const have = new Set(s || []);
        const add = nodesKey.filter((k) => !have.has(k));
        return add.length ? [...(s || []), ...add] : s;
      });
      setClickedChild((s) => {
        if (keyIsCtrl) {
          if (s.find((mesh) => mesh.UniqueId === clicked.UniqueId)) {
            const neededIndex = s.findIndex(
              (mesh) => mesh.UniqueId === clicked.UniqueId
            );
            return [...s.slice(0, neededIndex), ...s.slice(neededIndex + 1)];
          }
          return [...s, clicked];
        } else {
          return [clicked];
        }
      });
      // THAT COMMENT WAS WRONG AND I WROTE IT. The row's DOM id is the
      // node's OWN id: TreeViewerItem destructures `id` from the item and
      // renders id={id}. The full slash path is the library's `key`, which
      // openNodes uses - two different identifiers, and I had the scroll
      // target on the wrong one. The owner's trace says it plainly:
      //   END: pick: done {rowInDom: false, key: "1/13/12/12::Ladder .../3ad.."}
      //   model-view/scroll {tries: 21, rowInDom: false, rowsRendered: 0}
      // rowsRendered counted elements whose id contains "/" and found none,
      // because no row's id ever contains one. nodesKey still opens the
      // branch; scrollTargetId names the row.
      trace.step("pick: setClickedChild", { uid: clicked && clicked.UniqueId ? String(clicked.UniqueId).slice(0, 12) : null });
      const rowId = scrollTargetId(path);
      if (rowId) requestScroll(rowId);

      WebGL.current.viewer.onClickChildInList(
        clicked ? clicked.UniqueId : "",
        keyIsCtrl,
        selectors,
        false,
        false // shouldFrameCamera
      );
      selectedMesh.current = clicked.UniqueId;
      // AFTER THE FRAME: did the row actually appear and light up? This is the
      // step every previous attempt was missing - the difference between
      // "found" and "shown".
      if (typeof requestAnimationFrame === "function") {
        const key = rowId;
        requestAnimationFrame(() => {
          const el = typeof document !== "undefined" && key ? document.getElementById(key) : null;
          trace.end("pick: done", {
            rowInDom: !!el,
            selected: !!(el && /amber/.test(el.className || "")),
            key: String(key || "").slice(0, 40),
          });
        });
      } else {
        trace.end("pick: done (no raf)");
      }
    }
  };

  const findInJsonUsingViewer = async (rawId, keyIsCtrl) => {
    // A NESTED PART IS NOT A THING ON ITS OWN: clicking a nut, a washer
    // or a rod means clicking the hanger it belongs to. Since Tom's fix
    // of 2026-09-24 those parts are drawn as their own instance rows with
    // their own uids and NO record - so without this line, clicking one
    // showed nothing at all. It showed the nut before the merge and the
    // hanger after it; nothing was never an option anybody chose.
    // Identified POSITIVELY from the parent the exporter puts on the row,
    // never from "this uid has no record" - absence is also what a failed
    // fetch and an older export look like.
    // A MISS IS NOT A FAILURE (Tom, 2026-10-20). The viewer calls this with
    // null when the ray hit nothing - _reportPickMiss - and the whole chain
    // below then ran on an id that does not exist: the no-row dialog, a
    // report to /client-log and a GET /model-tree/locate?uniqueId=null that
    // could only ever 404. Clicking empty space looked exactly like a broken
    // pick, in the logs and to the user. Clear the selection and return.
    const tr = startTrace("model-view-click");
    tr.step("handler entry", { rawId: rawId || null, ctrl: !!keyIsCtrl });
    if (!rawId) {
      tr.end("pick miss (ray hit nothing)");
      setClickedFilesChild([]);
      setNoMeshReason("");
      setModal(false);
      return;
    }
    const id = resolveToElement(rawId);
    tr.step("resolveToElement", { id, changed: id !== rawId });

    // Highlight in the model first, and do it synchronously.
    //
    // Everything below is about the TREE: which row to open, which
    // record the details panel shows. On a document whose records live
    // in the worker that means a fetch and a parse - seconds, the first
    // time, for a 300 MB record file - and the 3D selection has no
    // reason to wait for it. The element the user clicked is highlighted
    // now; the row and the panel catch up.
    const filesSelectors = {
      prevMeshes: "prevFilesMeshes",
      prevMaterials: "prevFilesMaterials",
      color: "green",
    };
    //
    // Only for a plain click. A ctrl-click is a toggle, and the tree
    // path below issues the same call again, which would turn the
    // selection straight back off.
    if (!keyIsCtrl) {
      const liveViewer = WebGL.current && WebGL.current.viewer;
      if (liveViewer && typeof liveViewer.onClickChildInList === "function") {
        try {
          liveViewer.onClickChildInList(id, false, filesSelectors, false, false);
        } catch (e) {
          // The tree path below is still worth trying.
        }
      }
    }

    // Project files - from the refs, because this closure was captured at mount
    // and the raw arrays are empty by the time anybody clicks.
    const loadedFiles = filesMeshesRef.current || [];
    const loadedTrees = treeFilesRef.current || [];
    tr.step("refs", {
      files: loadedFiles.length,
      filesWithRows: loadedFiles.filter((x) => x && x.length).length,
      trees: loadedTrees.filter(Boolean).length,
      lazyDocs: lazyDocsRef.current.length,
    });
    const { perFile: clickedFilesMesh, found: eagerHit } = findClickedMesh(loadedFiles, id);
    tr.step("eager lookup", { found: eagerHit });

    // An indexed document keeps its records in the worker, so a click in
    // the model asks which document holds the element, opens the row it
    // sits under, and then picks the leaf that appeared. The array keeps
    // one slot per document either way: the tree walk below branches on
    // its length.
    let insertedLeafId = null;
    let treeDirty = false;
    if (!clickedFilesMesh.some(Boolean) && lazyDocsRef.current.length) {
      // Ask the document the uid names, and only that one.
      // "" for a SHARED prefix as well as an unknown one: asking the one
      // document that happens to have claimed it last and finding nothing
      // there is how a clicked element reads as having no model.
      const owner = documentForUid(prefixOwnersRef.current, id);
      const order = owner
        ? [
          ...lazyDocsRef.current.filter((d) => d.docId === owner),
          ...lazyDocsRef.current.filter((d) => d.docId !== owner),
        ]
        : lazyDocsRef.current;
      // Only the named document is asked when we know which it is. The
      // rest are a fallback for an export whose prefixes we could not
      // learn, not a routine walk.
      const toAsk = owner ? order.slice(0, 1) : order;
      for (const { docId, docIndex } of toAsk) {
        let hit = null;
        try {
          hit = await findRecord(docId, id);
        } catch (e) {
          // The worker refused the file for its size rather than failing
          // to find the record: say which document and how big, and stop.
          // The element stays highlighted in 3D; only the details are
          // unavailable. Without this the tab used to die in the parse.
          if (isRecordTooLarge(e)) {
            antdMessage.error(recordTooLargeMessage(docId, e), 8);
            return;
          }
          hit = null;
        }
        tr.step("findRecord", { docId: String(docId).slice(0, 8), hit: !!hit });
        if (!hit) continue;
        const node = findLazyNodeByKey(loadedTrees[docIndex], hit.key);
        tr.step("lazy node", { docIndex, key: String(hit.key).slice(0, 24), nodeFound: !!node });
        // The group's full expansion is NOT on the click path any more - it is
        // ~5s of fetch and parse for a row we already have. Kick it off so the
        // group fills behind the person, and do not wait for it.
        if (node && !node.lazy?.loaded) { loadLazyNode(node); }
        hit.component.__docId = docId;
        // Past the row's first 2,000 the record is not listed, and the
        // tree walk below would find no path to it. See ensureLeafForRecord.
        // THE LEAF'S ID IS THE ONE THE TREE WILL RENDER IT UNDER, and it is
        // not always $id - leafFromRecord derives one when the export has
        // none. Keep it, so the walk below asks for the identity the node was
        // actually built with instead of guessing at a field.
        const inserted = node ? ensureLeafForRecord(node, hit.component, { allowUnloaded: true }) : null;
        if (inserted && inserted.id) {
          insertedLeafId = inserted.id;
          treeDirty = true;
          // THE INSERT IS A MUTATION, AND REACT CANNOT SEE ONE. The leaf is
          // pushed into the node's `nodes` array in place - every other
          // consumer reads `nodes` directly, so that is deliberate - but the
          // state array holding the tree keeps its identity, the memo that
          // builds the rows does not re-run, and the row never renders. Then
          // there is nothing to highlight and nothing for the scroll to find,
          // which is indistinguishable from not having inserted it at all.
          // The identity refresh itself is deferred to the batch below: done
          // here it was a full tree render of its own, BEFORE the batched one,
          // and the owner's trace of 2026-10-21 shows that gap - 5,474ms
          // between "lazy node" and the pick, while the batched steps that
          // follow it all land in the same millisecond. One render, not two.
        }
        clickedFilesMesh[docIndex] = hit.component;
        break;
      }
    }

    if (!clickedFilesMesh.some(Boolean)) {
      // Nothing to show. Say so only once there was something to search:
      // on a legacy project the records may still be on their way.
      const searched =
        lazyDocsRef.current.length || loadedFiles.some((file) => file && file.length);
      // NOT THE SAME THING AS "THERE IS NO MODEL" (owner, 2026-10-20). If the
      // element is drawn in 3D - which it is, he just clicked it - then the
      // data exists and we failed to find its row. Saying "there is no model
      // associated with this component" is a false statement to the user's
      // face, and on a 1.39M-element export most of the model is never
      // resident, so it fired constantly. Say which it is.
      setNoMeshReason(
        searched
          ? "This element is in the model but its details have not loaded yet. Give it a moment, or open the document in the tree to load that part."
          : "The project records are still loading. Try again in a moment."
      );
      setModal(true);
      // REPORTED TO THE SERVER, NOT JUST THE CONSOLE (leader, 2026-10-20).
      // Four attempts at this bug were diagnosed by reading code and all four
      // were wrong; the owner should not have to copy a console line out of
      // his browser for the fifth. /client-log already exists for exactly
      // this - a fault on somebody else's machine read on the server rather
      // than described over the phone. Throttled to 10/min by the helper and
      // sent only when a click finds NOTHING, so it cannot become noise.
      const diag = {
        uniqueId: id,
        rawId: rawId !== id ? rawId : undefined,
        filesLoaded: loadedFiles.length,
        filesWithRows: loadedFiles.filter((x) => x && x.length).length,
        treesLoaded: loadedTrees.filter(Boolean).length,
        lazyDocs: lazyDocsRef.current.length,
        prefixes: prefixOwnersRef.current ? prefixOwnersRef.current.owner.size : 0,
        sharedPrefixes: prefixOwnersRef.current ? prefixOwnersRef.current.shared.size : 0,
        searched: !!searched,
      };
      tr.step("no eager/lazy row", diag);
      console.warn("[model-view] no tree row for the clicked element", diag);
      reportClientError("model-view/no-tree-row", { message: JSON.stringify(diag) });

      // THE LEGACY LOOP HAS NOTHING, SO ASK THE SERVER (owner, 2026-10-20:
      // "build the manifest-backed tree"). A streaming export has no
      // per-document JSONs, which is why everything above found nothing. The
      // commit still holds every element, so /model-tree/locate answers in
      // one indexed query - measured at 3 ms on BA03's 1,386,689 rows - with
      // the exact chain of keys to open and which page the leaf falls on.
      try {
        tr.step("server tree: locate");
        const loc = await treeLocate(projectId, id);
        tr.step("server tree: located", { cat: loc.element.category, page: loc.page });
        const open = openKeysFromLocate(loc);
        const leaf = leafKeyFromLocate(loc);
        // Build only the branch that was clicked: category, its family, its
        // type and the page of elements holding this one. Four requests, not
        // a walk of 102 categories.
        const [fams, types, leaves] = await Promise.all([
          treeChildren(projectId, { category: loc.element.category }),
          treeChildren(projectId, { category: loc.element.category, family: loc.element.family }),
          treeChildren(projectId, {
            category: loc.element.category,
            family: loc.element.family,
            type: loc.element.typeName,
            skip: loc.page * loc.pageSize,
            limit: loc.pageSize,
          }),
        ]);
        const branch = serverBranch(loc, fams, types, leaves);
        serverTreeRef.current = branch;
        setServerTree(branch);
        setFilesOpenNodes((prev) => {
          const next = new Set([...(prev || []), ...open]);
          return [...next];
        });
        setServerLeafKey(leaf);
        setIsFilesMeshes(true);
        // SELECTION IS clickedChild, NOT A KEY. The row tests
        // clickedChild.find(c => c.UniqueId === meshInfo.UniqueId), so the
        // selected element has to be put there as an object.
        setClickedFilesChild(keyIsCtrl
          ? (prev) => {
            const have = (prev || []).some((m) => m && m.UniqueId === id);
            return have ? prev.filter((m) => m && m.UniqueId !== id) : [...(prev || []), { UniqueId: id, Name: loc.element.name }];
          }
          : [{ UniqueId: id, Name: loc.element.name }]);
        // THE SCROLL TARGET IS THE ROW'S DOM ID, which react-simple-tree-menu
        // sets to the node's FULL slash path - not the bare leaf key. Passing
        // the leaf key meant getElementById found nothing and the tree never
        // scrolled, which looked exactly like "selection does not work".
        requestScroll(leafRowIdFromLocate(loc));
        tr.end("server branch opened", { leaf: String(leaf).slice(0, 40) });
        // DID THE ROW ACTUALLY TAKE? Reported after a frame, because the row
        // is drawn by the render this click triggers. "Found but not
        // selected" is precisely what the owner is describing and the server
        // could not see it.
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => {
            const el = typeof document !== "undefined" ? document.getElementById(leafRowIdFromLocate(loc)) : null;
            reportClientError("model-view/row-state", {
              message: JSON.stringify({ uniqueId: id, leafKey: leaf, rowInDom: !!el, selected: !!(el && /amber/.test(el.className || "")) }),
            });
          });
        }
        setModal(false);
        setNoMeshReason("");
        return;
      } catch (e) {
        if (e && e.status === 404) {
          setNoMeshReason(
            "This element is drawn in the model but is not in the published commit this page is reading. Re-publish, or pick the matching version."
          );
        } else {
          setNoMeshReason(
            "This element is in the model but its details could not be read just now. Try again in a moment."
          );
          console.warn("[model-view] model-tree locate failed", e && e.message);
        }
        setModal(true);
      }
      return;
    }

    if (clickedFilesMesh) {
      // THE SUCCESS PATH REPORTS TOO (Tom, 2026-10-20: "a click that DOES
      // find the record sends no report"). Without this the server sees only
      // failures, so "the row was found but not selected" - which is what the
      // owner is describing - is invisible. One line per resolved click,
      // throttled by the helper like any other report.
      tr.step("record found -> pickMeshInNeededTree", { viaLazy: !!lazyDocsRef.current.length });
      reportClientError("model-view/row-found", {
        message: JSON.stringify({
          uniqueId: id,
          viaLazy: !!lazyDocsRef.current.length,
          treeSections: loadedTrees.filter(Boolean).length,
        }),
      });
      // EVERY UPDATE THIS CLICK MAKES, IN ONE RENDER.
      //
      // This whole path runs after `await findRecord(...)`, and React 16 only
      // batches updates inside its own event handlers - not after an await.
      // So each of these four flags, the open nodes, the selection, the tree
      // identity and the scroll request rendered the tree SEPARATELY: a dozen
      // full re-renders of a tree the menu library re-walks each time. That
      // is the hang, and the forced tree-identity refresh I added for the
      // row to appear at all made it worse. unstable_batchedUpdates is the
      // supported escape hatch for exactly this case on 16 and becomes a
      // no-op on 18's automatic batching.
      batch(() => {
      if (treeDirty) setTreeFilesData((prev) => (Array.isArray(prev) ? prev.slice() : prev));
      setIsFilesMeshes(true);
      setIsDemolitionMeshes(false);
      setIsExistingMeshes(false);
      setIsUnmatchedMeshes(false);
      // THE TRACE WAS NEVER PASSED IN HERE. My instrumentation added `tr` to
      // the OTHER call site - the one with an inline selectors object - and
      // missed this one, which uses the filesSelectors variable and is the
      // call every real click takes. So pickMeshInNeededTree fell back to its
      // no-op tracer, end() did nothing, and the owner's clicks reported
      // row-found and then nothing at all. The silence was my wiring, not the
      // code under test.
      //
      // The try/catch stays regardless: a throw inside the pick would
      // otherwise end the chain with no report, which is exactly the
      // ambiguity this instrumentation exists to remove.
      try {
        pickMeshInNeededTree(
          clickedFilesMesh,
          loadedTrees.filter((item) => item),
          setFilesOpenNodes,
          setClickedFilesChild,
          keyIsCtrl,
          filesSelectors,
          tr,
          insertedLeafId
        );
      } catch (e) {
        tr.end("pick THREW", { message: String((e && e.message) || e).slice(0, 120) });
        throw e;
      }
      });
    }
  };

  // Fetch the revision being reviewed.
  useEffect(() => {
    let alive = true;
    if (!diffCommitId) { setDiffData(null); return undefined; }
    getCommitDiff(diffCommitId, { limit: 4000 })
      .then((r) => {
        const body = r && r.data ? r.data : r;
        // api/index.js's get() resolves error bodies too.
        if (alive) setDiffData(body && body.summary ? body : null);
      })
      .catch(() => { if (alive) setDiffData(null); });
    return () => { alive = false; };
  }, [diffCommitId]);

  // The geometry behind the diff, for the panel's before/after pictures.
  // Deliberately separate from the diff fetch: it reads tile sidecars and
  // can be slower, and the change list must not wait on it.
  useEffect(() => {
    let alive = true;
    if (!diffCommitId) { setEditFragments(null); return undefined; }
    getCommitEditFragments(diffCommitId, { limit: 600 })
      .then((r) => {
        const body = r && r.data ? r.data : r;
        if (alive) setEditFragments(body && body.fragments ? body : null);
      })
      .catch(() => { if (alive) setEditFragments(null); });
    return () => { alive = false; };
  }, [diffCommitId]);

  // Paint it once the model is up. `loading` flips false when the
  // viewer reports itself loaded, which is also when the store exists.
  useEffect(() => {
    if (loading || !diffData || diffHidden) return undefined;
    const viewer = WebGL.current && WebGL.current.viewer;
    if (!viewer) return undefined;
    // Go through diffElementRows rather than diffData.elements directly:
    // it is what splits `moved` out of `modified`, so the overlay paints
    // a relocation blue like the legend says instead of amber - and it
    // is the same list the panel is showing, which is what makes the
    // bucket filter mean the same thing in both places.
    const rows = diffElementRows(diffData);
    const shown = diffBucket ? rows.filter((r) => r.change === diffBucket) : rows;
    // Everything not in `shown` falls through to the overlay's ghost
    // sweep, which is exactly "ghost everything outside the selected tab".
    const cleanup = applyDiffOverlay(viewer, shown, diffData.changedTilesByCategory);
    return cleanup || undefined;
  }, [loading, diffData, diffHidden, diffBucket]);

  const decideRevision = async (action) => {
    setDiffBusy(action);
    try {
      const fn = action === "approve" ? approveRevision : rejectRevision;
      await fn(diffCommitId, {});
      addNotification(
        action === "approve"
          ? "Revision approved — it is now part of the project history"
          : "Revision sent back — it stays out of the project history",
        "success"
      );
      history.push("/approval-queue");
    } catch (e) {
      addNotification(e?.err || e?.message || `Could not ${action} the revision`, "danger");
    } finally {
      setDiffBusy("");
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    if (!projectsRedux && !line) {
      if (isMountedRef.current) {
        cacheRequest();
      }
    }
    return () => (isMountedRef.current = false);
  }, [projectsRedux]);

  useEffect(() => {
    isMountedRef.current = true;
    // ONE IMPLEMENTATION, SHARED (owner, 2026-10-21: "why don't all these
    // systems use the same viewer base"). This page's private copy had a real
    // fault that the staging check found and his clicks never would have:
    // when the tree container does NOT scroll - a short list, everything
    // already visible - the walk up ends with box = null, and the very next
    // line called box.getBoundingClientRect(). An unhandled rejection, after
    // the row had been selected, so the selection looked fine and the page
    // was broken. The shared helper reports boxFound:false and returns.
    const cancel = scrollRowToCentre(scrollId && scrollId.key, {
      isAlive: () => isMountedRef.current,
      report: (r) => reportClientError("model-view/scroll", { message: JSON.stringify(r) }),
    });
    return () => { cancel(); isMountedRef.current = false; };
  }, [scrollId]);

  useEffect(() => {
    if (cacheErrorStatus) {
      const stringError = String(cacheErrorStatus);
      addNotification(stringError, "danger");
      if (stringError.toLowerCase()?.includes("unexpected token")) {
        logOut();
        history.push("/");
      }
    }
  }, [cacheErrorStatus]);

  const infoPanel = (
    <div
      style={{
        minWidth: `${isSecondScreen ? resize?.panels[1] || 600 : ""}px`,
        maxWidth: `${isSecondScreen ? resize?.panels[1] || 600 : ""}px`,
      }}
      className="viewer-outer-div"
    >
      <div className="px-2 d-flex justify-content-between align-items-center overflow-hidden viewer-header-item viewer-gapping">
        <div className="d-flex align-items-center viewer-header-item ">
          <img src={estimationImg} className="estimation-icon" />
          Component Details:
        </div>

        <MDBBox
          onClick={() => setSecondScreen((s) => !s)}
          className={`viewer-icon ${
            !isSecondScreen ? "viewer-icon-rotate" : ""
          }`}
        >
          <CloseIcon />
        </MDBBox>
      </div>
    </div>
  );

  const ProjectPanel = (
    <div
      style={{
        minWidth: `${isFirstScreen ? resize?.panels[2] || 600 : ""}px`,
        maxWidth: `${isFirstScreen ? resize?.panels[2] || 600 : ""}px`,
      }}
      className="viewer-outer-div"
    >
      <div className="px-2 d-flex justify-content-between align-items-center overflow-hidden viewer-header-item viewer-gapping">
        <MDBBox className="viewer-header-item d-flex align-items-center">
          <img src={projectIcon} className="project-icon" />
          Project Files
        </MDBBox>
        <MDBBox
          onClick={() => setFirstScreen((s) => !s)}
          className={`viewer-icon ${
            !isFirstScreen ? "viewer-icon-rotate" : ""
          }`}
        >
          <CloseIcon />
        </MDBBox>
      </div>
    </div>
  );

  return (
    <div className="timeline-viewer-container mt-2">
      {/* a publish while this is open (plugin or Models-page export) offers a reload */}
      <LiveRevisionWatch projectId={projectId} enabled={!token} />
      <MDBBox className="viewer d-flex overflow-hidden flex-wrap">
        <MDBBox className="viewer-header viewer-header1 d-flex w-100">
          {isFirstScreen && isSecondScreen && (
            <>
              {ProjectPanel}
              {infoPanel}
            </>
          )}

          {!isFirstScreen && isSecondScreen && (
            <>
              {infoPanel}
              {ProjectPanel}
            </>
          )}
          {isFirstScreen && !isSecondScreen && (
            <>
              {ProjectPanel}
              {infoPanel}
            </>
          )}

          {!isFirstScreen && !isSecondScreen && (
            <>
              {ProjectPanel}
              {infoPanel}
            </>
          )}

          <div className="d-flex align-items-center justify-content-end flex-grow-1 px-2 viewer-header-item viewer-gapping h-100">
            {project ? (
              <MDBBox tag="span" className="viewer-header-item">
                <div className="d-flex align-items-center project-admin-header ">
                  <i className="fa fa-home mr-2"></i> Project:
                  <strong className="ml-2">{project.title}</strong>
                </div>
              </MDBBox>
            ) : null}
            {/* The Filters button was commented out, which is why the old
                filter panel below has been unreachable: `menu` is false and
                nothing flips it. This opens the NEW bar instead - the old
                panel speaks a vocabulary we are replacing. */}
            <button
              className="bg-bid header-btn text-white ml-2 font-weight-light"
              onClick={() => setFilterOpen((v) => !v)}
              title="Filter the model by category, level, type, size or status"
            >
              <i className="fa fa-filter mr-2" />
              Filter
            </button>
          </div>
        </MDBBox>

        {/* THE BAR MUST NOT MOVE THE VIEWER. In the flow it added its own
            height to everything below it - measured at three viewport sizes,
            the viewer's top went 109 -> 188 and its height did NOT shrink, so
            the navigation cluster at the bottom was pushed further off the
            screen by exactly the bar's 79px. The owner could not reach the
            navigation buttons with the filter open. The dock is a zero-height
            anchor and the bar floats inside it, so opening the filter changes
            nothing about where anything else sits. */}
        {filterOpen && (
          <div className="model-filter-bar-dock">
            <ModelFilterBar
              projectId={projectId}
              onIsolate={isolateUniqueIds}
              onShowEverything={showEverythingInDiff}
              onClose={() => setFilterOpen(false)}
            />
          </div>
        )}

        {/* resizable main container starts */}
        <ModelResizableViewer
          width="100%"
          height="100%"
          WebGlApp={WebGL.current}
          setResize={setResize}
          isFirstScreen={isFirstScreen}
          isSecondScreen={isSecondScreen}
          allowToResize={allowToResize}
        >
          {/* project files container starts */}
          <div style={{ height: "100%" }}>
            <ResizablePanels
              displayDirection="column"
              width="100%"
              maxheight="100%"
              panelsSize={0}
              resizerSize="3px"
              sizeUnitMeasure="%"
            >
              <MDBBox className="overflow-auto h-76vh inner_resizable">
                {jsonFilesMeshesToJSX}
                {loading ? (
                  <MDBBox className="h-100">
                    <Loading color="black" text="Loading components..." />
                  </MDBBox>
                ) : null}
              </MDBBox>
              <MDBBox></MDBBox>
            </ResizablePanels>
          </div>

          {/* estimation-info container starts */}
          <MDBBox className="overflow-auto h-76vh inner_resizable">
            <MDBListGroup>
              {jsonInfo && (
                <RecordFieldRows
                  data={jsonInfo}
                  omit={["$id", "TotalPrice", "UnitPrice"]}
                />
              )}
            </MDBListGroup>

            {/* Additional-info container starts */}
            {additionalFileInfo?.length === 1 && (
              <>
                <div className="px-2 d-flex justify-content-between align-items-center overflow-hidden viewer-header-item viewer-gapping">
                  <div className="d-flex align-items-center viewer-header-item ">
                    <img src={estimationImg} className="estimation-icon" />
                    Additional Details:
                  </div>
                </div>

                <MDBListGroup>
                  <MDBListGroupItem className="border border-light py-1 text-truncate">
                    {`Name: ${additionalFileInfo[0]?.Name}`}
                  </MDBListGroupItem>

                  <RecordFieldRows
                    data={additionalFileInfo[0]?.BuiltInParameters}
                    idPrefix="BuiltInParameters."
                  />

                  <RecordFieldRows
                    data={additionalFileInfo[0]?.EquipmentParameters}
                    idPrefix="EquipmentParameters."
                  />

                  <RecordFieldRows
                    data={additionalFileInfo[0]?.FamilyParameters}
                    idPrefix="FamilyParameters."
                  />

                  <RecordFieldRows
                    data={additionalFileInfo[0]?.GlobalParameters}
                    idPrefix="GlobalParameters."
                  />

                  <RecordFieldRows
                    data={additionalFileInfo[0]?.ProjectParameters}
                    idPrefix="ProjectParameters."
                  />

                  <RecordFieldRows
                    data={additionalFileInfo[0]?.SharedParameters}
                    idPrefix="SharedParameters."
                  />
                </MDBListGroup>
              </>
            )}

            {/* Electrical-info container starts */}
            {electricalFileInfo?.length === 1 && (
              <>
                <div className="px-2 d-flex justify-content-between align-items-center overflow-hidden viewer-header-item viewer-gapping">
                  <div className="d-flex align-items-center viewer-header-item ">
                    <img src={estimationImg} className="estimation-icon" />
                    Electrical Systems:
                  </div>
                </div>

                <MDBListGroup>
                  {electricalFileInfo?.map((item, i) => (
                    <RecordFieldRows
                      key={i}
                      data={item}
                      idPrefix={`el${i}.`}
                    />
                  ))}
                </MDBListGroup>
              </>
            )}

            {/* Mechanical-info container starts */}
            {mechanicalFileInfo?.length === 1 && (
              <>
                <div className="px-2 d-flex justify-content-between align-items-center overflow-hidden viewer-header-item viewer-gapping">
                  <div className="d-flex align-items-center viewer-header-item ">
                    <img src={estimationImg} className="estimation-icon" />
                    Mechanical Systems:
                  </div>
                </div>
                <MDBListGroup>
                  {mechanicalFileInfo?.map((item, i) => (
                    <RecordFieldRows
                      key={i}
                      data={item}
                      idPrefix={`me${i}.`}
                    />
                  ))}
                </MDBListGroup>
              </>
            )}
          </MDBBox>

          {/* 3d modal and share container starts */}
          <div className="h-100 position-relative">
            <ResizablePanels
              displayDirection="column"
              width="100%"
              height="100%"
              panelsSize={[0, 100]}
              resizerSize={menu ? "10px" : "0px"}
              resizerColor="#E6EAF4"
              sizeUnitMeasure="%"
              ref={resizeRef}
            >
              <QuantityContext.Provider
                value={{
                  Currency: currencysymb,
                  meshQuantityInfoRef: meshQuantityInfoRef.current,
                }}
              >
                <div className="share-container-new">
                  <div
                    className="share-wrappre"
                    style={menu ? {} : { display: "none" }}
                  >
                    <div className="share">
                      {/* Unreachable (see the Filter button above) but wired
                          correctly regardless: this component takes setTreeData
                          and treeData, and was being handed setTreeFilesData
                          and no treeData - so RunFilter and its reset would
                          throw the moment either ran. Share.jsx passes these
                          two; these are the same two. */}
                      <Filters
                        FiltersData={FiltersArr}
                        setTreeData={setTreeFilesData}
                        treeData={treeFilesData}
                        WebGL={WebGL}
                      />
                    </div>
                  </div>
                </div>
              </QuantityContext.Provider>

              <div
                id="WebglViewer"
                style={{ flexGrow: 1 }}
                className="h-100 position-relative"
                ref={viewerRef}
              >
                {loading ? (
                  <MDBBox className="h-100 w-100 flex-center flex-column">
                    <Loading color="black" text="Loading model..." />
                    {percentsLoaded ? (
                      <span>{percentsLoaded}% has loaded...</span>
                    ) : null}
                  </MDBBox>
                ) : null}

                {/* Revision review overlay (?diff=<commitId>). The rest of
                    the viewer keeps working underneath it — tree, isolate,
                    hide/show, section, measure — so a reviewer can walk the
                    change the same way they walk the model. */}
                {diffData ? (
                  <div
                    style={{
                      position: "absolute", top: 12, right: 12, zIndex: 20,
                      width: 250, background: "rgba(255,255,255,.96)",
                      border: "1px solid #E3E7EA", borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(0,0,0,.12)", padding: 12,
                      fontFamily: "Inter, system-ui, sans-serif",
                    }}
                  >
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", color: "#7C8B96" }}>
                      {diffData.meta?.reviewStatus === "pending" ? "REVISION UNDER REVIEW" : "MODEL CHANGE"}
                    </div>
                    {/* Provenance and the "old stats" the change is
                        measured against — a reviewer needs to know whose
                        push this is and what it replaced, not just how
                        many things moved. */}
                    {diffData.meta ? (
                      <div style={{ fontSize: 11.5, color: "#526470", margin: "4px 0 8px", lineHeight: 1.45 }}>
                        <div style={{ color: "#20303C", fontWeight: 600 }}>
                          {diffData.meta.label || diffData.meta.revitDocTitle || "Model push"}
                        </div>
                        <div>
                          {diffData.meta.author?.name || diffData.meta.author?.email || "Unknown author"}
                          {diffData.meta.ts ? ` · ${new Date(diffData.meta.ts).toLocaleString()}` : ""}
                        </div>
                        <div>
                          {diffData.meta.elementCount} element{diffData.meta.elementCount === 1 ? "" : "s"} in this push
                          {diffData.meta.revitVersion ? ` · Revit ${diffData.meta.revitVersion}` : ""}
                          {diffData.meta.pluginVersion ? ` · plugin ${diffData.meta.pluginVersion}` : ""}
                        </div>
                        {diffData.meta.baseline ? (
                          <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px dashed #E3E7EA" }}>
                            <b style={{ color: "#20303C" }}>Compared against</b>
                            <div>
                              {diffData.meta.baseline.label || diffData.meta.baseline.revitDocTitle || "previous push"}
                              {diffData.meta.baseline.ts ? ` · ${new Date(diffData.meta.baseline.ts).toLocaleDateString()}` : ""}
                            </div>
                            <div>{diffData.meta.baseline.elementCount} element{diffData.meta.baseline.elementCount === 1 ? "" : "s"} then</div>
                          </div>
                        ) : (
                          <div style={{ marginTop: 4 }}>No earlier snapshot to compare against.</div>
                        )}
                      </div>
                    ) : null}
                    <div style={{ fontSize: 11.5, color: "#526470", margin: "0 0 8px" }}>
                      {diffData.scope === "selection"
                        ? "A selection push — it carries only the elements the author chose, so it cannot report deletions."
                        : "A full model push."}
                    </div>
                    {diffLegendRows(diffData.summary).map((r) => (
                      <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, fontSize: 12 }}>
                        <span style={{ width: 11, height: 11, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                        <span style={{ flex: 1, color: "#526470" }}>{r.label}</span>
                        <b style={{ color: "#20303C" }}>{r.count}</b>
                      </div>
                    ))}
                    <div style={{ fontSize: 11, color: "#7C8B96", margin: "6px 0 10px", lineHeight: 1.35 }}>
                      Everything else is ghosted, not hidden — the change is
                      shown in the model it lands in.
                    </div>
                    {diffCoverageNote(diffData) ? (
                      <div style={{ fontSize: 11, color: "#874D00", background: "#FFF7E6", border: "1px solid #FFD591", borderRadius: 4, padding: "5px 7px", margin: "0 0 10px", lineHeight: 1.35 }}>
                        {diffCoverageNote(diffData)} Only the listed elements are coloured in the model.
                      </div>
                    ) : null}
                    <MDBBtn
                      size="sm"
                      color=""
                      style={{ width: "100%", margin: "0 0 8px", background: "#20303C", color: "#fff" }}
                      onClick={() => setDiffListOpen(true)}
                    >
                      What changed — element by element
                    </MDBBtn>
                    {diffData.meta?.reviewStatus === "pending" ? (
                      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                        <MDBBtn
                          size="sm" color="" style={{ flex: 1, margin: 0, background: "#1F4E63", color: "#fff" }}
                          disabled={!!diffBusy}
                          onClick={() => decideRevision("approve")}
                        >
                          {diffBusy === "approve" ? "Merging…" : "Approve & merge"}
                        </MDBBtn>
                        <MDBBtn
                          size="sm" color="" style={{ flex: 1, margin: 0, background: "#C53030", color: "#fff" }}
                          disabled={!!diffBusy}
                          onClick={() => decideRevision("reject")}
                        >
                          {diffBusy === "reject" ? "Sending…" : "Send back"}
                        </MDBBtn>
                      </div>
                    ) : null}
                    {diffIsolatedId && (
                      <button
                        type="button"
                        onClick={showEverythingInDiff}
                        style={{
                          width: "100%", border: "1px solid #D8E3E9", background: "#F3F7F9",
                          borderRadius: 6, fontSize: 11.5, padding: "5px 0", cursor: "pointer",
                          color: "#20303C", fontWeight: 600, marginBottom: 6,
                        }}
                      >
                        Show everything again (one element is isolated)
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDiffHidden((v) => !v)}
                      style={{
                        width: "100%", border: "1px solid #E3E7EA", background: "#fff",
                        borderRadius: 6, fontSize: 11.5, padding: "4px 0", cursor: "pointer", color: "#526470",
                      }}
                    >
                      {diffHidden ? "Show the change again" : "Clear the overlay (keep reviewing)"}
                    </button>
                  </div>
                ) : null}
                {/* TOP RIGHT, the same corner on every page (owner,
                    2026-09-20). It was mounted inside the .viewer-zoom
                    group below - `bottom: 56px`, no left or right - so it
                    inherited that group's BOTTOM-LEFT corner. Its own
                    positioned box makes the corner a property of the strip
                    rather than of whatever it was dropped into.
                    #WebglViewer is position-relative; these offsets are
                    measured from it. */}
                <div
                  className="position-absolute"
                  style={{ top: 12, right: 12, zIndex: 5 }}
                >
                  <ViewerViewButtons
                    viewer={() => WebGL.current && WebGL.current.viewer}
                    variant="mdb"
                  />
                </div>
                {/* THE toolbar, not this page's copy of it. Everything that used to
                    be hand-written here - refresh, zoom, screenshot, home,
                    zoom-to-selected, grid, measure, section, isolate and the
                    gear - now comes from components/Viewer/ViewerToolbar, which
                    every other 3D page renders too (owner, 2026-09-23: "same
                    icons ... and same location for all views, literally copy the
                    same exact code"). The ids the key handlers reach for move
                    with it, and only ONE element may carry each.
                    The two below stay: they are genuinely this page's, driving
                    its streaming store and a debug overlay. */}
                <ViewerToolbar viewer={() => WebGL.current && WebGL.current.viewer}>
                <MDBBtn
  id="enableDisableElementsBtn"
  title="Enable/Disable Elements — toggle visibility of components in the Disabled tree"
  size="sm"
  color=""
  className="bg-white modal3d-tool-btn"
  onClick={(e) => {
    // ModelViewer doesn't have a C# WS counterpart, so just drive the
    // local store. enumerateDisabledUids walks pool registry to find
    // every currently-disabled uid. Toggle their visibility.
    const willShow = !e.currentTarget.classList.contains('buttonEnabled');
    e.currentTarget.classList.toggle('buttonEnabled');
    try {
      const store = WebGL.current?.viewer?.streaming?.state;
      const enumerator = WebGL.current?.viewer?.streaming?.enumerateDisabledUids;
      if (store && enumerator) {
        const uids = enumerator();
        uids.forEach(uid => {
          if (willShow) store.showDisabled(uid);
          else store.hideDisabled(uid);
        });
      }
    } catch (err) {
      console.warn('[EnableDisable] apply failed:', err);
    }
  }}
>
  <MDBIcon icon="power-off" className="font-size-1" />
</MDBBtn>
<MDBBtn
  id="outlierBboxesToggleBtn"
  title="Toggle tile bounding-box wireframe overlay (debug)"
  size="sm"
  color=""
  className="bg-white modal3d-tool-btn"
  onClick={(e) => {
    WebGL.current.viewer.streaming.toggleShowOutlierBboxes();
    e.currentTarget.classList.toggle('buttonEnabled');
  }}
>
  <MDBIcon icon="border-style" className="font-size-1" />
</MDBBtn>
                </ViewerToolbar>
              </div>
            </ResizablePanels>
          </div>
        </ModelResizableViewer>

        <NoMeshModal isOpen={modal} reason={noMeshReason} toggle={() => { setModal((s) => !s); setNoMeshReason(""); }} />

        <Drawer
          title="What changed"
          placement="right"
          width={430}
          open={diffListOpen}
          onClose={() => setDiffListOpen(false)}
          // The viewer keeps working behind it: isolate a row, look at
          // it, pick the next one - without the drawer closing.
          mask={false}
          styles={{ body: { padding: 12 } }}
        >
          {diffData ? (
            <DiffElementPanel
              diff={diffData}
              onBucketChange={setDiffBucket}
              editFragments={editFragments}
              onIsolate={isolateDiffElement}
              onShowEverything={showEverythingInDiff}
              isolatedId={diffIsolatedId}
              onCreateIssue={raiseIssueFromDiff}
              creatingIssue={creatingDiffIssue}
              height="calc(100 * var(--vh) - 130px)"
            />
          ) : null}
        </Drawer>
      </MDBBox>
    </div>
  );
};

export default ModelViewer;